import { getRouteApi } from '@tanstack/react-router';
import { Checkbox } from 'cpool-components/input/Checkbox';
import { APPLICANT_ATTRIBUTION_BOTH } from 'cpool-constants';
import type { LOAN_REQUEST_COMMAND } from 'cpool-constants';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { Credit, LoanType } from '../../../types';
import { getCommandName } from '../../utils/getCommandName';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useLead from '~/features/leadDetails/hooks/useLead';
import { canExecuteForAttribution } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    credit: Credit;
    index: number;
    prefix: string;
    toggleRefinance: (index: number) => void;
    showDetailedView: boolean;
    nonFinanceable?: boolean;
    creditKind: LoanType;
};

export const RefinanceCell: React.FC<Props> = ({
    credit,
    creditKind,
    index,
    nonFinanceable = false,
    prefix,
    showDetailedView,
    toggleRefinance,
}) => {
    const { t } = useTranslation();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { data: lead } = useLead(leadId);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const excludeApplicant1 = useWatch({
        name: 'dataSnippets.applicant1.excludeApplicant',
    }) as boolean;
    const excludeApplicant2 = useWatch({
        name: 'dataSnippets.applicant2.excludeApplicant',
    }) as boolean;

    const commandName = getCommandName({ action: 'change', loanType: creditKind }) as LOAN_REQUEST_COMMAND;
    const applicant2NoPermission =
        credit.gehoertZuAntragsteller === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2 &&
        !canExecuteForAttribution({ attribution: credit.gehoertZuAntragsteller, commandName, permissions });

    const applicant1NoPermission =
        credit.gehoertZuAntragsteller === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1 &&
        !canExecuteForAttribution({ attribution: credit.gehoertZuAntragsteller, commandName, permissions });

    const isDisabled = showDetailedView || nonFinanceable || applicant2NoPermission || applicant1NoPermission;

    const applicant2DataProtectionAgreed = lead?.antragsteller2?.dataProtectionAgreed;
    const applicant2Hidden = lead?.coApplicantHidden;

    let permissionMessage = t('LEAD_DETAILS_GENERAL.NO_PERMISSION_TO_CHANGE_LIABILITY_APPLICANT2');
    if (!applicant2DataProtectionAgreed) {
        permissionMessage = t('LEAD_DETAILS_GENERAL.DATA_PROTECTION_AGREEMENT_WARNING');
    }
    if (applicant2Hidden) {
        permissionMessage = t('LEAD_DETAILS_GENERAL.SECOND_APPLICANT_HIDDEN_WARNING');
    }

    if (applicant2NoPermission) {
        return (
            <td className="absolute inset-0 inline-flex items-center justify-end mr-[7vw] text-grey pointer-events-none z-100 bg-gradient-to-r from-transparent via-gray-200 to-gray-200">
                <span className="pr-6">{permissionMessage}</span>
            </td>
        );
    }

    if (excludeApplicant1 && credit.gehoertZuAntragsteller === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1) {
        return (
            <td className="absolute inset-0 inline-flex items-center justify-end mr-[7vw] text-grey pointer-events-none z-100 bg-gradient-to-r from-transparent via-gray-200 to-gray-200">
                <span className="pr-6">{t('LEAD_DETAILS_GENERAL.FIRST_APPLICANT_EXCLUSION_WARNING')}</span>
            </td>
        );
    }
    if (excludeApplicant2 && credit.gehoertZuAntragsteller === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2) {
        return (
            <td className="absolute inset-0 inline-flex items-center justify-end mr-[7vw] text-grey pointer-events-none z-100 bg-gradient-to-r from-transparent via-gray-200 to-gray-200">
                <span className="pr-6">{t('LEAD_DETAILS_GENERAL.SECOND_APPLICANT_EXCLUSION_WARNING')}</span>
            </td>
        );
    }

    if (credit.hide) {
        return (
            <td className="absolute inset-0 inline-flex items-center justify-end mr-[7vw] text-grey pointer-events-none z-100 bg-gradient-to-r from-transparent via-gray-200 to-gray-200">
                <span className="pr-6">{t('BANK_CREDIT_REQUEST.REFINANCE_PLANNER.NOT_SENT_TO_BANK')}</span>
            </td>
        );
    }

    return (
        <td onClick={(e) => e.stopPropagation()}>
            <Checkbox
                id={`${prefix}-refinance-checkbox-${index}`}
                aria-label={t('BANK_CREDIT_REQUEST.REFINANCE_PLANNER.REFINANCE_CHECKBOX')}
                checked={'abloesen' in credit ? credit.abloesen ?? false : false}
                onChange={() => toggleRefinance(index)}
                disabled={isDisabled}
            />
        </td>
    );
};
